const toPlain = (data) => {
  if (data == null) return data;
  if (typeof data.asMutable === "function") return data.asMutable({ deep: true });
  if (typeof data.toJS === "function") return data.toJS();
  return data;
};

export const planIdOf = (item) => {
  if (item == null || item === "") return "";
  if (typeof item === "number" || typeof item === "string") return String(item);
  const nested = item.plan && typeof item.plan === "object" ? item.plan : null;
  const id = item.id ?? item.planId ?? nested?.id ?? nested?.planId;
  return id == null || id === "" ? "" : String(id);
};

export const normalizePlan = (item) => {
  if (item == null) return null;
  if (typeof item === "number" || typeof item === "string") {
    return { id: item };
  }
  const nested = item.plan && typeof item.plan === "object" ? item.plan : null;
  const id = planIdOf(item);
  if (nested) {
    return {
      ...item,
      ...nested,
      id: id || nested.id || item.id,
      linkedOn: item.linkedOn ?? nested.linkedOn,
      linkReason: item.linkReason ?? nested.linkReason,
    };
  }
  return id ? { ...item, id } : item;
};

export const uniquePlans = (items) => {
  const seen = new Set();
  const out = [];
  (items || []).forEach((raw) => {
    const plan = normalizePlan(raw);
    if (!plan) return;
    const id = planIdOf(plan);
    if (id) {
      if (seen.has(id)) return;
      seen.add(id);
    }
    out.push(plan);
  });
  return out;
};

export const plansFromPayload = (data) => {
  const plain = toPlain(data);
  if (Array.isArray(plain)) return uniquePlans(plain);
  if (!plain || typeof plain !== "object") return [];
  const nested = [
    plain.content,
    plain.plans,
    plain.items,
    Array.isArray(plain.data) ? plain.data : null,
    plain._embedded?.plans,
    plain._embedded?.planList,
  ];
  for (const value of nested) {
    if (Array.isArray(value)) return uniquePlans(value);
  }
  if (plain.id != null || plain.planId != null || plain.plan) {
    return uniquePlans([plain]);
  }
  return [];
};

export const parseLinkedPlanCount = (data) => {
  const plain = toPlain(data);
  if (typeof plain === "number" && Number.isFinite(plain)) return plain;
  if (typeof plain === "string" && plain !== "" && Number.isFinite(Number(plain))) {
    return Number(plain);
  }
  if (Array.isArray(plain)) {
    const extracted = uniquePlans(plain);
    return extracted.length || plain.length;
  }
  if (plain && typeof plain === "object") {
    const value = plain.count ?? plain.value ?? plain.total ?? plain.totalElements
      ?? (typeof plain.data === "number" ? plain.data : null);
    if (value != null && Number.isFinite(Number(value))) return Number(value);
    const nested = plansFromPayload(plain);
    if (nested.length) return nested.length;
  }
  return 0;
};

const cacheKey = (customerId) => `reveknew.linked-plans.${customerId}`;

export const readLinkedPlansCache = (customerId) => {
  if (!customerId || typeof sessionStorage === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(cacheKey(customerId));
    const parsed = raw ? JSON.parse(raw) : [];
    return uniquePlans(Array.isArray(parsed) ? parsed : []);
  } catch {
    return [];
  }
};

export const writeLinkedPlansCache = (customerId, plans) => {
  if (!customerId || typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(cacheKey(customerId), JSON.stringify(uniquePlans(plans)));
  } catch {
    // ignore quota / private-mode failures
  }
};

export const mergeLinkedPlansCache = (customerId, plans) => {
  const merged = uniquePlans([...(readLinkedPlansCache(customerId) || []), ...(plans || [])]);
  writeLinkedPlansCache(customerId, merged);
  return merged;
};
